import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, Button } from "../../shared";
import DocumentationLayout from "../../layouts/DocumentationLayout";
import { SidebarContainer } from "../../components";
import helpCoverageRegistry from "@/features/help-hub/coverage/coverage.registry";
import {
  isFeatureEnabled,
  isFeatureAvailableForAppType,
  buildDocsUrl,
  canOpenDocsPlugin,
} from "@/features/help-hub/docs.contract";
import { useAuth, useConfig } from "@/hooks";
import { getFeatureHelpPath } from "@/features/help-hub/help-links";
import { getFeatureIcon } from "@/features/help-hub/feature-icons";

const GettingStarted = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { configs, appType } = useConfig();
  const featureFlags = configs?.featureFlags || {};
  const [activeSectionId, setActiveSectionId] = useState();

  const availableSections = useMemo(
    () =>
      helpCoverageRegistry
        .filter((item) => isFeatureEnabled(featureFlags, item.featureKey))
        .filter((item) => isFeatureAvailableForAppType(item.appTypes, appType)),
    [featureFlags, appType]
  );

  const walkthroughSections = useMemo(
    () => availableSections.filter((item) => item.helpKind !== "page"),
    [availableSections]
  );

  const pageGuides = useMemo(
    () => availableSections.filter((item) => item.helpKind === "page"),
    [availableSections]
  );

  useEffect(() => {
    const scrollToCurrentHash = () => {
      const hashValue = window.location.hash || location.hash;
      const sectionId = decodeURIComponent((hashValue || "").replace("#", ""));
      const fallbackSectionId = walkthroughSections[0]?.helpSectionId;
      const targetId = sectionId || fallbackSectionId;
      if (!targetId) return;
      const target = document.getElementById(targetId);
      if (!target) return;
      setActiveSectionId(targetId);
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    scrollToCurrentHash();
    const raf = requestAnimationFrame(scrollToCurrentHash);
    window.addEventListener("hashchange", scrollToCurrentHash);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("hashchange", scrollToCurrentHash);
    };
  }, [location.hash, walkthroughSections]);

  useEffect(() => {
    const nodes = walkthroughSections
      .map((section) => document.getElementById(section.helpSectionId))
      .filter(Boolean);
    if (!nodes.length || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (!visible.length) return;
        const id = visible[0].target?.id;
        if (id) setActiveSectionId(id);
      },
      {
        root: null,
        rootMargin: "-80px 0px -60% 0px",
        threshold: [0.1, 0.25, 0.5],
      }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [walkthroughSections]);

  const featureMap = useMemo(() => {
    return availableSections.reduce((acc, section) => {
      acc[section.id] = section;
      return acc;
    }, {});
  }, [availableSections]);

  return (
    <SidebarContainer
      hideSidebar
      useViewportHeight={false}
      className="h-full bg-white"
      sidebarTitle="Documentation"
    >
      {() => (
        <DocumentationLayout
          sections={walkthroughSections}
          activeSectionId={activeSectionId}
        >
          <div className="w-full py-4 pr-2">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Feature Guides
            </h1>

            <div className="prose prose-lg">
              <p className="lead text-xl text-gray-600">
                Use this page as your complete in-app walkthrough reference. Every
                routable feature section includes prerequisites, step-by-step usage,
                branching decisions, and success checks.
              </p>

              {pageGuides.length > 0 && (
                <div className="mt-6 not-prose rounded-lg border border-primary-200 bg-primary-50 p-4">
                  <h2 className="text-lg font-semibold text-primary-900">
                    Searchable reference
                  </h2>
                  <p className="mt-1 text-sm text-primary-800">
                    Look up real-world trucking words without leaving the app, or
                    open the published docs library for full-site search.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pageGuides.map((guide) => (
                      <Link key={guide.id} to={getFeatureHelpPath(guide)}>
                        <Button type="primary" size="sm">
                          {guide.title}
                        </Button>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-gray-900">
                  Jump to a section
                </h2>
                <div className="mt-4 flex flex-wrap gap-2 not-prose">
                  {availableSections.map((section) => (
                    <Link
                      key={section.id}
                      to={getFeatureHelpPath(section)}
                      className="text-sm rounded-full border border-gray-300 px-3 py-1.5 text-gray-700 hover:border-primary-500 hover:text-primary-700"
                    >
                      {section.title}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-gray-900">Guides by module</h2>
                <div className="space-y-4 mt-4 not-prose">
                  {walkthroughSections.map((section) => (
                    <div
                      key={section.id}
                      id={section.helpSectionId}
                      className="scroll-mt-28"
                    >
                      <Card className="p-4">
                        <div className="flex items-start gap-3">
                          <span className="text-primary-600 shrink-0 mt-0.5">
                            {getFeatureIcon(section.id, "h-6 w-6")}
                          </span>
                          <div className="w-full">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {section.title}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {section.description}
                            </p>
                            <p className="text-sm text-gray-700 mt-3">
                              {section.overview || section.helpSectionSummary}
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Recommended roles
                                </h4>
                                <div className="flex flex-wrap gap-1.5">
                                  {(section.roles || []).map((role) => (
                                    <span
                                      key={`${section.id}-${role}`}
                                      className="text-xs rounded-full bg-gray-100 text-gray-700 px-2 py-1"
                                    >
                                      {role}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Prerequisites
                                </h4>
                                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                                  {(section.prerequisites || []).map((item) => (
                                    <li key={item}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Step-by-step walkthrough
                                </h4>
                                <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-1">
                                  {(section.stepByStep || []).map((item) => (
                                    <li key={item}>{item}</li>
                                  ))}
                                </ol>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Decision points
                                </h4>
                                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                                  {(section.decisionPoints || []).map((item) => (
                                    <li key={`${section.id}-${item}`}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Success checks
                                </h4>
                                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                                  {(section.validationChecks || []).map((item) => (
                                    <li key={`${section.id}-${item}`}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>

                            <div className="mt-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                FAQ
                              </h4>
                              <div className="space-y-2">
                                {(section.faq || []).map((item) => (
                                  <div
                                    key={`${section.id}-${item.question || item}`}
                                    className="rounded border border-gray-200 p-2"
                                  >
                                    <p className="text-xs font-semibold text-gray-900">
                                      {item.question || "Question"}
                                    </p>
                                    <p className="text-xs text-gray-700 mt-1">
                                      {item.answer || item}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {Boolean(section.relatedFeatures?.length) && (
                              <div className="mt-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Related features
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {section.relatedFeatures.map((relatedId) => {
                                    const related = featureMap[relatedId];
                                    if (!related) return null;
                                    return (
                                      <Link
                                        key={`${section.id}-${relatedId}`}
                                        to={getFeatureHelpPath(related)}
                                        className="text-xs rounded-full border border-gray-300 px-2 py-1 text-gray-700 hover:border-primary-500 hover:text-primary-700"
                                      >
                                        {related.title}
                                      </Link>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            <div className="mt-4 flex flex-wrap gap-2">
                              <Link to={section.inAppPath}>
                                <Button type="primary" size="sm">
                                  Open in app
                                </Button>
                              </Link>
                              {canOpenDocsPlugin(section.docsPluginId, user) && (
                                <a
                                  href={buildDocsUrl({
                                    docsPluginId: section.docsPluginId,
                                    docsSlug: section.docsSlug,
                                  })}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Button type="alternative" size="sm">
                                    Open docs
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 bg-primary-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-primary-900 mb-3">
                  Need Help?
                </h3>
                <p className="text-primary-800">
                  For account-specific support, create a feedback ticket and our
                  support team will follow up.{" "}
                  <Link
                    to="/feedback/create"
                    className="text-primary-600 hover:text-primary-800 underline"
                  >
                    Contact support
                  </Link>{" "}
                  any time.
                </p>
              </div>
            </div>
          </div>
        </DocumentationLayout>
      )}
    </SidebarContainer>
  );
};

export default GettingStarted;
